import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CmtsReduxThunkExtra, CmtsSerializableError } from "./thunkExtra.js";
import { serializeReduxError } from "./thunkExtra.js";

export type CmtsOrgContext = {
  readonly orgUnitId?: string | undefined;
  readonly orgUnitCode?: string | undefined;
  readonly orgUnitName?: string | undefined;
  readonly siteId?: string | undefined;
  readonly siteCode?: string | undefined;
};

export type CmtsTenantState = {
  readonly tenantId?: string | undefined;
  readonly tenantCode?: string | undefined;
  readonly tenantName?: string | undefined;
  readonly timezoneCode: string;
  readonly orgContext?: CmtsOrgContext | undefined;
  readonly loading: boolean;
  readonly error?: CmtsSerializableError | undefined;
  readonly lastResolvedAtUtc?: string | undefined;
};


export const fetchCmtsTenantContext = createAsyncThunk<Partial<CmtsTenantState>, void, { extra: CmtsReduxThunkExtra; rejectValue: CmtsSerializableError }>(
  "tenant/fetchContext",
  async (_arg, thunkApi) => {
    try {
      if (!thunkApi.extra.authRepository) {
        return thunkApi.rejectWithValue({ message: "Auth repository is not available for tenant context refresh.", atUtc: new Date().toISOString() });
      }
      const me = await thunkApi.extra.authRepository.me();
      return {
        tenantId: me.tenantContext.tenantId,
        tenantName: me.tenantContext.tenantName,
        timezoneCode: me.tenantContext.timezoneCode ?? "Asia/Kolkata",
      };
    } catch (error: unknown) {
      return thunkApi.rejectWithValue(serializeReduxError(error, "Tenant context refresh failed."));
    }
  },
);

const initialState: CmtsTenantState = {
  timezoneCode: "Asia/Kolkata",
  loading: false,
};

const tenantSlice = createSlice({
  name: "tenant",
  initialState,
  reducers: {
    tenantResolveStarted(state) {
      state.loading = true;
      state.error = undefined;
    },
    tenantResolved(state, action: PayloadAction<Partial<CmtsTenantState>>) {
      state.tenantId = action.payload.tenantId ?? state.tenantId;
      state.tenantCode = action.payload.tenantCode ?? state.tenantCode;
      state.tenantName = action.payload.tenantName ?? state.tenantName;
      state.timezoneCode = action.payload.timezoneCode ?? state.timezoneCode;
      state.orgContext = action.payload.orgContext ?? state.orgContext;
      state.loading = false;
      state.error = undefined;
      state.lastResolvedAtUtc = new Date().toISOString();
    },
    tenantResolveFailed(state, action: PayloadAction<CmtsSerializableError>) {
      state.loading = false;
      state.error = action.payload;
    },
    tenantCleared(state) {
      state.tenantId = undefined;
      state.tenantCode = undefined;
      state.tenantName = undefined;
      state.timezoneCode = "Asia/Kolkata";
      state.orgContext = undefined;
      state.loading = false;
      state.error = undefined;
      state.lastResolvedAtUtc = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCmtsTenantContext.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchCmtsTenantContext.fulfilled, (state, action) => {
        state.tenantId = action.payload.tenantId ?? state.tenantId;
        state.tenantCode = action.payload.tenantCode ?? state.tenantCode;
        state.tenantName = action.payload.tenantName ?? state.tenantName;
        state.timezoneCode = action.payload.timezoneCode ?? state.timezoneCode;
        state.orgContext = action.payload.orgContext ?? state.orgContext;
        state.loading = false;
        state.error = undefined;
        state.lastResolvedAtUtc = new Date().toISOString();
      })
      .addCase(fetchCmtsTenantContext.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? serializeReduxError(action.error, "Tenant context refresh failed.");
      });
  },
});

export const tenantActions = tenantSlice.actions;
export const tenantReducer = tenantSlice.reducer;
